if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const addonOptionGroups = this.getAddonOptionGroups();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        let groupedRequest = false;
        let linesUpdateDeferred = null;
        let variantId = null;
        let targetAddonGroupId = null;
        let sectionsToRender = [];

        try {
          addonOptionGroups.forEach((addonOptions) => addonOptions.clearError?.());

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          const formData = new FormData(this.form);
          variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;

          if (this.dataset.addonRequired === 'true' && addonOptionGroups.length === 0) {
            groupedRequest = true;
            throw Object.assign(
              new Error(
                this.dataset.addonRequiredError ||
                  'The required add-on options are unavailable. Please refresh and try again.'
              ),
              { code: 'INVALID' }
            );
          }

          if (addonOptionGroups.some((addonOptions) => typeof addonOptions.getSelectedAddons !== 'function')) {
            groupedRequest = true;
            throw new Error('The add-on options are unavailable. Please refresh and try again.');
          }

          const missingRequiredGroup = addonOptionGroups.find(
            (addonOptions) => addonOptions.required && !addonOptions.hasSelection
          );
          if (missingRequiredGroup) {
            groupedRequest = true;
            const requiredError =
              missingRequiredGroup.dataset.requiredError ||
              'Please select an add-on before adding this product.';
            missingRequiredGroup.showError?.(requiredError);
            missingRequiredGroup.focusFirstAvailable?.();
            throw Object.assign(new Error(requiredError), {
              code: 'INVALID',
              addonGroupErrorDisplayed: true,
            });
          }

          const selectedAddonsByGroup = addonOptionGroups.map((addonOptions) =>
            addonOptions.getSelectedAddons(quantity)
          );

          if (selectedAddonsByGroup.some((addons) => !Array.isArray(addons))) {
            throw new Error('The selected add-ons could not be read. Please try again.');
          }
          const selectedAddons = selectedAddonsByGroup.flat();

          addonOptionGroups.forEach((addonOptions) => addonOptions.setLoading?.(true));
          groupedRequest = selectedAddons.length > 0;
          let requestLines = [{ merchandiseId: variantId, quantity }];

          if (this.cart) this.cart.setActiveElement(document.activeElement);

          if (groupedRequest) {
            const parentProperties = this._getParentProperties(formData);
            const configuration = this.getAddonConfiguration(variantId, selectedAddons, parentProperties);
            targetAddonGroupId = configuration.id;
            sectionsToRender = this.cart
              ? this.cart.getSectionsToRender().map((section) => section.id)
              : [];

            const cartBeforeAdd = await this.fetchCartSnapshot();
            const incrementResult = await this._incrementMatchingConfiguration(
              cartBeforeAdd,
              variantId,
              quantity,
              configuration,
              sectionsToRender
            );

            if (incrementResult.success) {
              const response = incrementResult.result;
              const cartRenderResponse = this.getCartRenderResponse(response, true, targetAddonGroupId);
              this.resolveCartLinesUpdate(linesUpdateDeferred);

              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: cartRenderResponse,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.renderCartContents(cartRenderResponse);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.renderCartContents(cartRenderResponse);
                });
              }
              return;
            }

            const items = this.createAddonItems(
              formData,
              selectedAddons,
              addonOptionGroups,
              variantId,
              quantity,
              configuration
            );

            config.body = JSON.stringify({
              items,
              sections: sectionsToRender,
              sections_url: window.location.pathname,
            });
            requestLines = items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity }));
          } else {
            delete config.headers['Content-Type'];
            if (this.cart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
            }
            config.body = formData;
          }

          linesUpdateDeferred = this.createCartLinesUpdateEvent(requestLines);

          const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
          let response = await fetchResponse.json();

          if (!fetchResponse.ok || response.status) {
            const errorMessage = this.getResponseErrorMessage(response);

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(errorMessage);
            if (groupedRequest) addonOptionGroups[0]?.showError?.(errorMessage);
            this.dispatchCartErrorEvent(errorMessage, 'INVALID');
            linesUpdateDeferred?.reject(new Error(errorMessage));

            // A grouped request may fail because a child is unavailable. That must
            // never turn the parent product button into a sold-out button.
            if (groupedRequest) {
              this.error = false;
              return;
            }

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            const soldOutText = this.submitButton.getAttribute('data-sold-out-message');
            
            if (soldOutText && soldOutText !== 'true') {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.textContent = soldOutText;
              this.error = true;
              return;
            } else if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }
            return;
          }

          if (!this.cart) {
            this.resolveCartLinesUpdate(linesUpdateDeferred);
            window.location = window.routes.cart_url;
            return;
          }

          const cartRenderResponse = this.getCartRenderResponse(response, groupedRequest, targetAddonGroupId);
          this.resolveCartLinesUpdate(linesUpdateDeferred);

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          if (!this.error)
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: cartRenderResponse,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure("add:paint-updated-sections", () => {
                    this.renderCartContents(cartRenderResponse);
                  });
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            CartPerformance.measure("add:paint-updated-sections", () => {
              this.renderCartContents(cartRenderResponse);
            });
          }
        } catch (e) {
          console.error(e);
          const errorMessage = e?.message || 'Network error';
          this.handleErrorMessage(errorMessage);
          if (groupedRequest && !e?.addonGroupErrorDisplayed) {
            addonOptionGroups[0]?.showError?.(errorMessage);
          }
          this.dispatchCartErrorEvent(errorMessage, e?.code || 'SERVICE_UNAVAILABLE');
          linesUpdateDeferred?.reject(e);
          this.error = false;
        } finally {
          try {
            addonOptionGroups.forEach((addonOptions) => addonOptions.setLoading?.(false));
          } catch (e) {
            console.error(e);
          }
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent("add:user-action", evt);
        }
      }

      getAddonOptionGroups() {
        // A form control named "id" shadows the native form.id property.
        // Read the attribute directly so every matching add-on group is found.
        const formId = this.form?.getAttribute('id');
        if (!formId) return [];

        return Array.from(
          document.querySelectorAll('product-addon-options[data-product-form-id]')
        ).filter(
          (element) => element.dataset.productFormId === formId
        );
      }

      getAddonOptions() {
        return this.getAddonOptionGroups()[0] || null;
      }

      _getParentProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[')) continue;
          const propName = key.replace(/^properties\[/, '').replace(/\]$/, '');
          properties[propName] = String(value);
        }
        return properties;
      }

      _getItemVisibleProperties(item) {
        const properties = item.properties || {};
        return Object.keys(properties)
          .filter((key) => !key.startsWith('_addon_') && !key.startsWith('_landing_') && !key.startsWith('_'))
          .sort()
          .map((key) => [key, String(properties[key])]);
      }

      getAddonConfiguration(mainVariantId, selectedAddons, parentProperties = {}) {
        const visibleProperties = Object.keys(parentProperties)
          .filter((key) => !key.startsWith('_addon_') && !key.startsWith('_landing_') && !key.startsWith('_'))
          .sort()
          .map((key) => [key, String(parentProperties[key])]);

        const giftIds = [
          ...new Set(
            selectedAddons
              .filter((addon) => addon.kind === 'free_gift' || String(addon.price) === '0')
              .map((addon) => String(addon.id))
          ),
        ].sort();

        const upsellIds = [
          ...new Set(
            selectedAddons
              .filter((addon) => addon.kind !== 'free_gift' && String(addon.price) !== '0')
              .map((addon) => String(addon.id))
          ),
        ].sort();

        const canonical = JSON.stringify({
          variant: String(mainVariantId),
          gifts: giftIds,
          upsells: upsellIds,
          properties: visibleProperties,
        });

        const hash = (seed) => {
          let value = seed >>> 0;
          for (let index = 0; index < canonical.length; index += 1) {
            value ^= canonical.charCodeAt(index);
            value = Math.imul(value, 16777619);
          }
          return (value >>> 0).toString(16).padStart(8, '0');
        };

        return {
          id: `addon_cfg_${hash(2166136261)}${hash(2246822519)}`,
          giftIds,
          upsellIds,
          visibleProperties,
        };
      }

      _getLegacyConfiguration(cart, parent) {
        const groupId = parent.properties && (parent.properties._addon_configuration_id || parent.properties._addon_group_id);
        if (!groupId) return null;

        const children = (cart.items || []).filter(
          (item) =>
            item.properties &&
            item.properties._addon_role === 'child' &&
            (item.properties._addon_group_id === groupId || item.properties._addon_configuration_id === groupId)
        );

        return {
          giftIds: children
            .filter((item) => item.properties._gift_of || item.properties._addon_kind === 'free_gift' || item.final_price === 0)
            .map((item) => String(item.variant_id))
            .sort(),
          upsellIds: children
            .filter((item) => item.properties._upsell_of || (item.properties._addon_kind !== 'free_gift' && item.final_price > 0))
            .map((item) => String(item.variant_id))
            .sort(),
          visibleProperties: this._getItemVisibleProperties(parent),
        };
      }

      _configurationMatches(cart, parent, configuration) {
        if (!parent.properties || parent.properties._addon_role !== 'parent') return false;
        if (parent.properties._addon_configuration_id === configuration.id || parent.properties._addon_group_id === configuration.id) {
          return true;
        }

        const legacy = this._getLegacyConfiguration(cart, parent);
        if (!legacy) return false;
        return (
          JSON.stringify(legacy) ===
          JSON.stringify({
            giftIds: configuration.giftIds,
            upsellIds: configuration.upsellIds,
            visibleProperties: configuration.visibleProperties,
          })
        );
      }

      async _incrementMatchingConfiguration(cart, mainVariantId, quantity, configuration, sectionsToRender) {
        const matches = (cart.items || []).filter(
          (item) =>
            !item.parent_relationship?.parent &&
            String(item.variant_id) === String(mainVariantId) &&
            this._configurationMatches(cart, item, configuration)
        );
        if (matches.length === 0) return { success: false };

        const keeper = matches[0];
        const updates = {
          [keeper.key]: matches.reduce((total, item) => total + Number(item.quantity || 0), quantity),
        };
        matches.slice(1).forEach((item) => {
          updates[item.key] = 0;
        });

        const rootPath =
          (window.routes && window.routes.root_url) ||
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
          '/';
        const cleanRoot = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
        const updateUrl = `${cleanRoot}cart/update.js`;

        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates,
            sections: sectionsToRender,
            sections_url: window.location.pathname,
          }),
        });

        let result;
        const rawText = await response.text();
        try {
          result = JSON.parse(rawText);
        } catch (e) {
          console.error('Cart update returned non-JSON response:', rawText);
          throw new Error('Unable to parse cart update response.');
        }

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to update the matching cart configuration.');
        }
        return { success: true, result };
      }

      createAddonItems(formData, selectedAddons, addonOptionGroups, variantId, quantity, configuration = null) {
        const parentVariantId = this.normalizeVariantId(variantId, 'main product');
        const parentQuantity = this.normalizeQuantity(quantity, 'main product');
        const parentProperties = this.getStringProperties(formData);
        const config = configuration || this.getAddonConfiguration(parentVariantId, selectedAddons, parentProperties);
        const groupId = config.id;
        const normalizedAddonGroups = Array.isArray(addonOptionGroups)
          ? addonOptionGroups
          : [addonOptionGroups].filter(Boolean);
        const addonSetNames = this.getUniqueAddonValues(
          selectedAddons,
          'setName',
          normalizedAddonGroups.map((addonOptions) => addonOptions?.dataset?.addonSetName)
        );
        const addonSetName = addonSetNames.join(' + ');
        const parentItem = {
          id: parentVariantId,
          quantity: parentQuantity,
          properties: {
            ...parentProperties,
            _addon_group_id: groupId,
            _addon_role: 'parent',
            _addon_configuration_id: groupId,
            _addon_set_name: addonSetName,
            _addon_expected_child_count: String(selectedAddons.length),
            _addon_gift_ids: config.giftIds.join(','),
            _addon_upsell_ids: config.upsellIds.join(','),
          },
        };
        const sellingPlan = formData.get('selling_plan');

        if (typeof sellingPlan === 'string' && sellingPlan) parentItem.selling_plan = sellingPlan;

        const childItems = selectedAddons.map((addon, index) => {
          const childVariantId = this.normalizeVariantId(addon?.id, `add-on ${index + 1}`);
          const childQuantity = this.normalizeQuantity(addon?.quantity, `add-on ${index + 1}`);
          const isFree = addon?.kind === 'free_gift' || String(addon?.price) === '0';

          const childProperties = {
            _addon_group_id: groupId,
            _addon_role: 'child',
            _addon_kind: String(addon?.kind || (isFree ? 'free_gift' : 'paid_addon')),
            _addon_quantity_policy: String(
              addon?.quantityPolicy || addon?.quantity_policy || (isFree ? 'fixed_one' : 'match_parent_quantity')
            ),
            _addon_parent_variant_id: parentVariantId,
            _addon_label: String(addon?.label || ''),
            _addon_configuration_id: groupId,
            _addon_set_name: String(addon?.setName || addonSetName),
          };

          if (isFree) {
            childProperties._gift_of = parentVariantId;
            childProperties._is_gift = 'true';
          } else {
            childProperties._upsell_of = parentVariantId;
          }

          return {
            id: childVariantId,
            quantity: childQuantity,
            parent_id: parentVariantId,
            properties: childProperties,
          };
        });

        return [parentItem, ...childItems];
      }

      getUniqueAddonValues(selectedAddons, propertyName, fallbackValues = []) {
        const selectedValues = selectedAddons
          .map((addon) => String(addon?.[propertyName] || '').trim())
          .filter(Boolean);
        const sourceValues = selectedValues.length > 0 ? selectedValues : fallbackValues;

        return Array.from(
          new Set(
            sourceValues
              .map((value) => String(value || '').trim())
              .filter(Boolean)
          )
        );
      }

      async fetchCartSnapshot() {
        const cartUrl = String(routes.cart_url || '/cart').replace(/\.js$/, '');
        const fetchResponse = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        const response = await fetchResponse.json();

        if (!fetchResponse.ok || response.status) {
          throw new Error(this.getResponseErrorMessage(response));
        }

        return response;
      }

      getCartRenderResponse(response, groupedRequest, targetGroupId = null) {
        if (!groupedRequest || !Array.isArray(response?.items)) return response;

        const parentLine =
          response.items.find(
            (item) =>
              item?.properties?._addon_role === 'parent' &&
              (!targetGroupId || item?.properties?._addon_group_id === targetGroupId)
          ) ||
          response.items.find((item) => item?.properties?._addon_role === 'parent') ||
          response.items[0];
        if (!parentLine) return response;

        // Dawn's drawer only needs `sections`, but cart-notification also expects
        // the top-level `id` and `key` shape returned by a single-line add.
        return {
          ...response,
          ...parentLine,
          items: response.items,
          sections: response.sections,
        };
      }

      renderCartContents(response) {
        try {
          this.cart.renderContents(response);
        } catch (error) {
          console.error(error);
          const refreshableCart = document.querySelector('cart-drawer-items, cart-items');
          refreshableCart?.onCartUpdate?.();
          this.handleErrorMessage(
            'The item was added, but the cart display could not refresh. Open the cart to review it.'
          );
        }
      }

      getStringProperties(formData) {
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const propertyMatch = name.match(/^properties\[(.*)\]$/);
          if (!propertyMatch) continue;

          if (typeof value === 'string') {
            properties[propertyMatch[1]] = value;
            continue;
          }

          if (value?.name || value?.size) {
            throw new Error(
              window.variantStrings?.addonFileUploadError ||
                'File uploads cannot be submitted together with add-ons. Remove the file or add the product without an add-on.'
            );
          }
        }

        return properties;
      }

      normalizeVariantId(value, label) {
        const variantId = String(value || '').trim();
        if (!/^\d+$/.test(variantId)) throw new Error(`The ${label} variant is invalid. Please refresh and try again.`);
        return variantId;
      }

      normalizeQuantity(value, label) {
        const quantity = Number(value);
        if (!Number.isInteger(quantity) || quantity < 1) {
          throw new Error(`The ${label} quantity is invalid. Please refresh and try again.`);
        }
        return quantity;
      }

      createAddonGroupId() {
        const cryptoApi = window.crypto;
        if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

        if (cryptoApi?.getRandomValues) {
          const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
          bytes[6] = (bytes[6] & 0x0f) | 0x40;
          bytes[8] = (bytes[8] & 0x3f) | 0x80;
          const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
          return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        }

        return `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      }

      getResponseErrorMessage(response) {
        if (typeof response?.description === 'string' && response.description) return response.description;
        if (typeof response?.message === 'string' && response.message) return response.message;
        if (typeof response?.errors === 'string' && response.errors) return response.errors;

        if (response?.errors && typeof response.errors === 'object') {
          const firstError = Object.values(response.errors).flat().find(Boolean);
          if (firstError) return String(firstError);
        }

        return 'The product could not be added to the cart. Please try again.';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(lines) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent || !lines?.length) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
